/**
 * movement system — v0M3.1.
 *
 * Advances every Army along its `plannedPath` at `armyEffectiveSpeed`
 * cells per tick. Waypoints are smoothed by the pathfinder (LOS-reduced
 * cell coords); this system walks the Army straight-line toward the
 * next waypoint, drops it when reached, and continues to the next if
 * the tick's movement budget isn't exhausted (so a fast army on a
 * short-hop waypoint list can pass through multiple waypoints in one
 * tick).
 *
 * When the Army arrives at its `movementGoal` (within `ARRIVAL_EPSILON`
 * cells) the plannedPath + goal are cleared.
 *
 * Ordering per MECHANICS.md §1.1: runs after `apply-commands` (so this
 * tick's `move_army` orders take effect immediately) and before
 * `zoc-detection` (so engagement triggers see updated positions —
 * `MAP.md` §2 / `MECHANICS.md` §3.2).
 *
 * v0M3.1 scope: pure position advance. Not yet: march stances (Forced
 * march ×1.35, Careful ×0.75), route preferences (Direct/Concealed),
 * fatigue accrual, supply drain, incremental repath on mid-march terrain
 * change. Each lands at v0M3.1a-d per TODO.md.
 */

import type { Command } from './commands';
import type { GameState, Army, Spy } from './state';
import { loadSpecialUnits } from '../data/special-units';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import { armyEffectiveSpeed, ARRIVAL_EPSILON, sortedIds } from './state';
import { getDialNumber, loadDials } from '../data/dials';
import { NEUTRAL_TEAM_ID } from './scenario';
import { WORLD_ROAD_MOVE_MULTIPLIER, WORLD_BRIDGE_MOVE_MULTIPLIER } from './apply-commands';
// MFix12.4 (2026-07-27) — pathfind + bridge overlay for waypoint chaining.
import { hierarchicalPath } from './pathfind';
import { computeBridgeCells } from './build';

// v0M3.5 — armies stop at ZoC-touching distance rather than overlapping.
// Threshold = 2 × zoc_radius. Loaded once at module init.
const DIALS = loadDials();
const ZOC_RADIUS = getDialNumber(DIALS, 'zoc_radius');
const ZOC_TOUCH_DISTANCE = 2 * ZOC_RADIUS;
// MFix14 (2026-07-29) — Settlements + Forts project a movement-blocking
// ZoC too (was only trigger-a-siege ZoC pre-fix). Circular per MECHANICS.md
// §3.2 amendment. The +0.5 slop matches sieges.ts::SIEGE_ADJACENCY_RADIUS
// so the siege trigger fires on the same tick the enemy army halts at the
// ZoC edge.
const SETTLEMENT_ZOC_RADIUS_MOVE = getDialNumber(DIALS, 'settlement_zoc_radius');

export function movementSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  // v0M5.3 (2026-07-25) — Spy movement (before armies for iteration
  // stability; order doesn't matter since spies don't ZoC-clamp against
  // anything). Simpler than armies: no ZoC touching, no engagement lock,
  // uses SpecialUnit.moveSpeedCampaign directly as per-tick budget.
  advanceSpies(state);
  // MFix11.2+.3 (2026-07-27) — after position advance, check every spy
  // with a pending escort-and-enter intent: fire the enter transition if
  // in range, else invalidate if the target's ownership drifted since
  // the order was issued.
  resolveSpyEnterOnArrival(state);
  // MFix12.4 (2026-07-27) — after any spy arrivals, pop next queued
  // waypoint (if any) and pathfind to it. Chain continues until queue is
  // empty. Runs after resolveSpyEnterOnArrival so a spy that just entered
  // a settlement won't get re-launched by a stale queued waypoint (the
  // enter transition doesn't clear waypoints — we do that here defensively).
  popNextWaypointsForSpies(state, context);

  // Deterministic iteration per MECHANICS.md §1.1 rule 3.
  for (const id of sortedIds(state.armies)) {
    const army = state.armies.get(id)!;
    // Engagement lock (v0M3.4) — armies locked in a battle do not advance,
    // and any pending path is discarded on lock. `zoc-detection` clears
    // `plannedPath` inside `createBattleFromArmies`, but we defensively
    // re-clear here in case a save/scenario boot ships an inconsistent
    // Army (currentBattleId set but plannedPath non-null). Also drop any
    // queued waypoints — a battle preempts the tour.
    if (army.currentBattleId !== null) {
      if (army.plannedPath !== null || army.movementGoal !== null) {
        army.plannedPath = null;
        army.movementGoal = null;
      }
      if (army.waypoints !== undefined) army.waypoints = undefined;
      continue;
    }
    if (army.plannedPath !== null && army.plannedPath.length > 0) {
      advanceArmy(army, state);
    }
    // MFix12.4 — after advance, if arrival cleared movementGoal AND the
    // army has a queued waypoint, pop it and pathfind to the new goal.
    // Loop in case a degenerate zero-length hop lands us at the next
    // waypoint immediately (bounded by queue length).
    maybePopNextWaypointArmy(army, state, context);
  }
  return state;
}

// ---------------------------------------------------------------------------
// MFix12.4 — waypoint chaining
// ---------------------------------------------------------------------------

/**
 * If `army` has arrived (movementGoal is null) AND its waypoints queue is
 * non-empty, pop the head, pathfind from current position, and set the new
 * movementGoal + plannedPath. On pathfind failure the head is discarded
 * silently and we try the next queued waypoint — degenerate/blocked
 * intermediate goals shouldn't halt the whole tour.
 */
function maybePopNextWaypointArmy(army: Army, state: GameState, context: SimContext): void {
  if (army.movementGoal !== null) return; // Still walking to current goal.
  if (!army.waypoints || army.waypoints.length === 0) return;
  const { width, height } = context.terrain;
  while (army.waypoints && army.waypoints.length > 0) {
    const head = army.waypoints[0]!;
    army.waypoints = army.waypoints.length > 1 ? army.waypoints.slice(1) : undefined;
    if (head.x < 0 || head.y < 0 || head.x >= width || head.y >= height) continue;
    const startX = Math.round(army.position.x);
    const startY = Math.round(army.position.y);
    const path = hierarchicalPath(
      context.terrain,
      context.regionGraph,
      startX,
      startY,
      head.x,
      head.y,
      computeBridgeCells(state, context.terrain.width),
      WORLD_BRIDGE_MOVE_MULTIPLIER,
    );
    if (!path.found) continue;
    const smoothed = path.smoothedCells;
    const waypoints = smoothed.length > 1 ? smoothed.slice(1) : smoothed.slice();
    army.plannedPath = waypoints;
    army.movementGoal = { x: head.x, y: head.y };
    return;
  }
}

/**
 * Pop-next for every spy that just arrived (movementGoal cleared) with
 * a non-empty waypoints queue.
 */
function popNextWaypointsForSpies(state: GameState, context: SimContext): void {
  if (!state.spies) return;
  const { width, height } = context.terrain;
  for (const id of sortedIds(state.spies)) {
    const spy = state.spies.get(id)!;
    if (spy.insideSettlementId) {
      // Spy is inside a settlement — waypoints don't apply. Clear defensively.
      if (spy.waypoints !== undefined) spy.waypoints = undefined;
      continue;
    }
    if (spy.movementGoal !== null) continue;
    if (!spy.waypoints || spy.waypoints.length === 0) continue;
    while (spy.waypoints && spy.waypoints.length > 0) {
      const head = spy.waypoints[0]!;
      spy.waypoints = spy.waypoints.length > 1 ? spy.waypoints.slice(1) : undefined;
      if (head.x < 0 || head.y < 0 || head.x >= width || head.y >= height) continue;
      const startX = Math.round(spy.position.x);
      const startY = Math.round(spy.position.y);
      const path = hierarchicalPath(
        context.terrain,
        context.regionGraph,
        startX,
        startY,
        head.x,
        head.y,
        computeBridgeCells(state, context.terrain.width),
        WORLD_BRIDGE_MOVE_MULTIPLIER,
      );
      if (!path.found) continue;
      const smoothed = path.smoothedCells;
      const waypoints = smoothed.length > 1 ? smoothed.slice(1) : smoothed.slice();
      spy.plannedPath = waypoints;
      spy.movementGoal = { x: head.x, y: head.y };
      break;
    }
  }
}

/**
 * Move `army` up to its per-tick speed budget along its plannedPath,
 * consuming waypoints as they're reached. v0M3.5 adds the stop-at-touch
 * clamp — before advancing to a new position, check enemy-army
 * proximity; if the step would put us within `ZOC_TOUCH_DISTANCE` of an
 * enemy army, clamp so we halt exactly at touching distance.
 */
// v0M5.3 (2026-07-25) — SpecialUnits table loaded once for the
// spy-speed lookup. Same pattern as apply-commands.ts.
const MOV_SPECIAL_UNITS = loadSpecialUnits();

/**
 * v0M5.3 — Advance every Spy along its plannedPath by up to
 * `moveSpeedCampaign` cells (from the SpecialUnits xlsx row) this
 * tick. No ZoC-clamp, no engagement lock — Spies never fight. Path
 * consumption + arrival semantics mirror advanceArmy.
 */
function advanceSpies(state: GameState): void {
  if (!state.spies) return;
  for (const id of sortedIds(state.spies)) {
    const spy = state.spies.get(id)!;
    if (spy.plannedPath === null || spy.plannedPath.length === 0) continue;
    const spec = MOV_SPECIAL_UNITS[spy.specialUnitType];
    // Fallback speed if the spec is missing (defensive; xlsx should carry it).
    const budgetCap = spec?.moveSpeedCampaign ?? 0.5;
    advanceSpyStep(spy, budgetCap);
  }
}

/**
 * MFix11.2+.3 (2026-07-27) — Post-move auto-enter check for every spy
 * carrying an `enterOnArrivalSettlementId` intent. Runs each tick after
 * `advanceSpies`.
 *
 * Three exit paths per spy:
 *   1. Target settlement has vanished (defensive) → clear intent silently.
 *   2. Province owner has changed since the intent was recorded → clear
 *      intent silently. Player's intent was formed under different
 *      conditions; better to abort than to enter an unexpected owner's
 *      city.
 *   3. Within Chebyshev 1.5 of the settlement cell → execute the enter
 *      transition (mirror of `enter_settlement_spy` semantics: clear
 *      path, set `insideSettlementId`, reset `infiltrationDays`, set
 *      `counterEspionage` per current ownership) and clear intent.
 *
 * Otherwise the spy keeps walking and the check re-runs next tick.
 */
function resolveSpyEnterOnArrival(state: GameState): void {
  if (!state.spies) return;
  for (const id of sortedIds(state.spies)) {
    const spy = state.spies.get(id)!;
    if (!spy.enterOnArrivalSettlementId) continue;
    if (spy.insideSettlementId) {
      // Defensive: spy is already inside (shouldn't happen since
      // enter_settlement_spy clears intent, but keep the guard).
      spy.enterOnArrivalSettlementId = null;
      spy.enterOnArrivalOwnerAtIntent = null;
      continue;
    }
    const targetSettlement = state.settlements.get(spy.enterOnArrivalSettlementId);
    if (!targetSettlement) {
      spy.enterOnArrivalSettlementId = null;
      spy.enterOnArrivalOwnerAtIntent = null;
      continue;
    }
    const targetProvince = state.provinces.get(targetSettlement.provinceId);
    const currentOwner = targetProvince?.ownerFactionId ?? null;
    const intentOwner = spy.enterOnArrivalOwnerAtIntent ?? null;
    if (currentOwner !== intentOwner) {
      // Ownership drifted since the player issued the escort order —
      // invalidate the intent. Spy keeps its path/goal so it still
      // walks toward the target cell, but won't auto-enter.
      spy.enterOnArrivalSettlementId = null;
      spy.enterOnArrivalOwnerAtIntent = null;
      continue;
    }
    const dx = Math.abs(spy.position.x - (targetSettlement.cell.x + 0.5));
    const dy = Math.abs(spy.position.y - (targetSettlement.cell.y + 0.5));
    if (Math.max(dx, dy) > 1.5) continue; // Not there yet.
    // Arrived — execute the enter transition (mirror of the
    // enter_settlement_spy handler body).
    spy.plannedPath = null;
    spy.movementGoal = null;
    spy.insideSettlementId = spy.enterOnArrivalSettlementId;
    spy.infiltrationDays = 0;
    const isFriendly = currentOwner === spy.ownerFactionId;
    spy.counterEspionage = isFriendly;
    spy.enterOnArrivalSettlementId = null;
    spy.enterOnArrivalOwnerAtIntent = null;
  }
}

function advanceSpyStep(spy: Spy, budgetInit: number): void {
  let budget = budgetInit;
  let path = spy.plannedPath;
  if (path === null) return;
  while (budget > 0 && path.length > 0) {
    const [wx, wy] = path[0]!;
    const dx = wx - spy.position.x;
    const dy = wy - spy.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const stepDist = Math.min(dist, budget);
    if (stepDist >= dist) {
      spy.position.x = wx;
      spy.position.y = wy;
      budget -= dist;
      path = path.slice(1);
      spy.plannedPath = path;
    } else {
      spy.position.x += (dx / dist) * stepDist;
      spy.position.y += (dy / dist) * stepDist;
      budget -= stepDist;
    }
  }
  // Arrival check — clear the goal if we're within epsilon of it.
  if (spy.movementGoal !== null) {
    const gdx = spy.movementGoal.x - spy.position.x;
    const gdy = spy.movementGoal.y - spy.position.y;
    if (Math.sqrt(gdx * gdx + gdy * gdy) <= ARRIVAL_EPSILON) {
      spy.movementGoal = null;
      spy.plannedPath = null;
    }
  }
  // Path exhausted but goal not yet reached — clear both to avoid
  // stuck-with-goal state.
  if (path === null || path.length === 0) {
    spy.plannedPath = null;
  }
}

function advanceArmy(army: Army, state: GameState): void {
  let budget = armyEffectiveSpeed(army, state);
  // v0M7.4b (2026-07-25) — Road speed bonus per MAP.md §9.2. When the
  // Army starts the tick on a Road cell (any faction — Road is
  // permanent-unowned), multiply the per-tick budget by
  // `1 / world_road_move_multiplier`. With the default multiplier of
  // 0.5, that doubles the budget. A fully-roaded province chain
  // compounds tick by tick because every tick starts on a road cell.
  // Off-road cells drop back to normal speed the next tick.
  const cx = Math.floor(army.position.x);
  const cy = Math.floor(army.position.y);
  if (hasRoadOnCell(state, cx, cy)) {
    budget = budget * (1 / WORLD_ROAD_MOVE_MULTIPLIER);
  }
  let path = army.plannedPath;
  if (path === null) return;

  const armyFaction = state.factions.get(army.ownerFactionId);
  const armyTeamId = armyFaction ? armyFaction.teamId : null;

  while (budget > 0 && path.length > 0) {
    const [wx, wy] = path[0]!;
    const dx = wx - army.position.x;
    const dy = wy - army.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Desired step size — either the full distance to the waypoint or
    // `budget`, whichever is smaller.
    const stepDist = Math.min(dist, budget);
    // Clamp step against enemy ZoC-touching distance.
    // MFix14 (2026-07-29) — clamp against both enemy Armies AND enemy
    // Settlements + Forts. Take the tightest constraint (min).
    const armyClamp = clampStepAgainstEnemies(
      state,
      armyTeamId,
      army.id,
      army.position.x,
      army.position.y,
      dx / dist,
      dy / dist,
      stepDist,
    );
    const settlementClamp = clampStepAgainstEnemySettlements(
      state,
      armyTeamId,
      army.position.x,
      army.position.y,
      dx / dist,
      dy / dist,
      armyClamp,
    );
    const clampedStep = clampStepAgainstEnemyForts(
      state,
      armyTeamId,
      army.position.x,
      army.position.y,
      dx / dist,
      dy / dist,
      settlementClamp,
    );
    if (clampedStep <= 0) {
      // Already touching or would immediately touch — halt without moving.
      break;
    }

    if (clampedStep >= dist) {
      // Reached the waypoint (either full step, or clamped past it —
      // former is normal, latter shouldn't happen but handled safely).
      army.position.x = wx;
      army.position.y = wy;
      budget -= dist;
      path = path.slice(1);
      army.plannedPath = path;
    } else {
      // Partial step (either budget-limited or ZoC-clamped).
      const stepX = (dx / dist) * clampedStep;
      const stepY = (dy / dist) * clampedStep;
      army.position.x += stepX;
      army.position.y += stepY;
      budget -= clampedStep;
      if (clampedStep < stepDist) {
        // ZoC-clamped — army halted before running out of budget or
        // reaching the waypoint. Drop remaining path — a future zoc-
        // detection tick will spawn the battle.
        army.plannedPath = null;
        army.movementGoal = null;
        return;
      }
      // Otherwise, budget-limited — the loop's `budget > 0` check will
      // exit naturally next iteration.
      break;
    }
  }

  // If we've reached (or passed) the final goal, clear the plan.
  if (army.movementGoal !== null) {
    const dxG = army.movementGoal.x - army.position.x;
    const dyG = army.movementGoal.y - army.position.y;
    const distG = Math.sqrt(dxG * dxG + dyG * dyG);
    if (distG <= ARRIVAL_EPSILON) {
      army.position.x = army.movementGoal.x;
      army.position.y = army.movementGoal.y;
      army.movementGoal = null;
      army.plannedPath = null;
    }
  }

  // If we've drained the waypoint list without reaching the goal, still
  // clear the plan (the pathfinder already returned success; overshoot
  // beyond the last waypoint would need a repath).
  if (army.plannedPath !== null && army.plannedPath.length === 0) {
    army.plannedPath = null;
    army.movementGoal = null;
  }
}

/**
 * Given a proposed movement step (unit direction + step distance) from
 * (x0, y0), return the largest step distance that keeps the moving army
 * at least `ZOC_TOUCH_DISTANCE` from every enemy army (different teamId,
 * non-neutral, not this army, not already in a battle).
 *
 * Returns the requested `stepDist` when no enemy is close enough to
 * clamp. May return 0 if the army is already at (or inside) touching
 * distance — halt with no move.
 */
function clampStepAgainstEnemies(
  state: GameState,
  ownTeamId: number | null,
  ownArmyId: Army['id'],
  x0: number,
  y0: number,
  dirX: number,
  dirY: number,
  stepDist: number,
): number {
  if (ownTeamId === null) return stepDist;
  let maxStep = stepDist;
  const threshold = ZOC_TOUCH_DISTANCE;
  const thresholdSq = threshold * threshold;
  for (const otherId of sortedIds(state.armies)) {
    if (otherId === ownArmyId) continue;
    const other = state.armies.get(otherId)!;
    // MFix13 (2026-07-29) — armies in an active battle STILL project a
    // blocking ZoC. Previously we skipped them here, which let a third
    // army walk right past an engaged pair (Michael's playtest bug). The
    // canon per MECHANICS.md §3.2 is that ZoC denies access
    // unconditionally; battle-locked armies are still on the map and
    // still occupy their circle. A third army halts at the touching
    // distance; when the underlying battle concludes, zoc-detection
    // will spawn the follow-on engagement on the next tick if the
    // third army is still parked at 2R (N-sided merge is v0M3.4-deferred
    // and stays deferred here).
    const otherFaction = state.factions.get(other.ownerFactionId);
    if (!otherFaction) continue;
    if (otherFaction.teamId === ownTeamId) continue;
    if (otherFaction.teamId === NEUTRAL_TEAM_ID) continue;

    // Solve for t ∈ [0, maxStep] where the moving position at
    // (x0 + t*dirX, y0 + t*dirY) is at squared distance `thresholdSq`
    // from (other.x, other.y). Quadratic in t:
    //   (x0 + t*dirX - ox)^2 + (y0 + t*dirY - oy)^2 = thresholdSq
    // → t^2 + 2t (dirX*(x0-ox) + dirY*(y0-oy)) + ((x0-ox)^2 + (y0-oy)^2 - thresholdSq) = 0
    const px = x0 - other.position.x;
    const py = y0 - other.position.y;
    const b = 2 * (dirX * px + dirY * py);
    const c = px * px + py * py - thresholdSq;
    if (c <= 0) {
      // Already at or inside touching distance — halt.
      return 0;
    }
    const disc = b * b - 4 * c;
    if (disc < 0) continue; // Never touches along this ray.
    const sqrtDisc = Math.sqrt(disc);
    // Smallest positive root gives the first touching t.
    const t1 = (-b - sqrtDisc) / 2;
    const t2 = (-b + sqrtDisc) / 2;
    const firstTouch = t1 >= 0 ? t1 : t2;
    if (firstTouch >= 0 && firstTouch < maxStep) {
      maxStep = firstTouch;
    }
  }
  return maxStep;
}

/**
 * MFix14 (2026-07-29) — Clamp the proposed movement step so the moving
 * army stays outside the ZoC circle of every enemy Settlement (city or
 * minor). Settlement ZoC is circular per Michael's spec, radius =
 * `settlement_zoc_radius` from the cell centre. An enemy Settlement is
 * one whose owning faction's team differs from the moving army's team;
 * neutral-owned settlements are excluded from the clamp (they still
 * fire the siege trigger, but they don't project a movement wall — a
 * neutral city sits between two hostile armies routinely at v1).
 *
 * Halt distance = `settlement_zoc_radius` exactly. `sieges.ts`'s
 * `SIEGE_ADJACENCY_RADIUS = settlement_zoc_radius + 0.5` guarantees
 * the siege trigger fires when the army halts here (halt distance ≤
 * trigger distance).
 */
function clampStepAgainstEnemySettlements(
  state: GameState,
  ownTeamId: number | null,
  x0: number,
  y0: number,
  dirX: number,
  dirY: number,
  stepDist: number,
): number {
  if (ownTeamId === null) return stepDist;
  let maxStep = stepDist;
  const threshold = SETTLEMENT_ZOC_RADIUS_MOVE;
  const thresholdSq = threshold * threshold;
  for (const settlementId of sortedIds(state.settlements)) {
    const settlement = state.settlements.get(settlementId);
    if (!settlement) continue;
    const province = state.provinces.get(settlement.provinceId);
    if (!province) continue;
    const ownerFactionId = province.ownerFactionId;
    if (ownerFactionId === null) continue; // Unowned neutral city — no wall.
    const ownerFaction = state.factions.get(ownerFactionId);
    if (!ownerFaction) continue;
    if (ownerFaction.teamId === ownTeamId) continue; // Own team — passes freely.
    if (ownerFaction.teamId === NEUTRAL_TEAM_ID) continue; // Neutral — no wall.

    // Settlement centre is (cell.x + 0.5, cell.y + 0.5). Match sieges.ts
    // adjacency semantics which uses cell corner; the +0.5 slop in
    // SIEGE_ADJACENCY_RADIUS absorbs the half-cell offset either way.
    const cx = settlement.cell.x + 0.5;
    const cy = settlement.cell.y + 0.5;
    const px = x0 - cx;
    const py = y0 - cy;
    const b = 2 * (dirX * px + dirY * py);
    const c = px * px + py * py - thresholdSq;
    if (c <= 0) return 0; // Already at/inside the ZoC — halt.
    const disc = b * b - 4 * c;
    if (disc < 0) continue;
    const sqrtDisc = Math.sqrt(disc);
    const t1 = (-b - sqrtDisc) / 2;
    const t2 = (-b + sqrtDisc) / 2;
    const firstTouch = t1 >= 0 ? t1 : t2;
    if (firstTouch >= 0 && firstTouch < maxStep) {
      maxStep = firstTouch;
    }
  }
  return maxStep;
}

/**
 * MFix14 (2026-07-29) — Peer of `clampStepAgainstEnemySettlements` for
 * enemy Forts. Same circular ZoC model. Fort ownership is stored on the
 * WorldStructure directly (no province indirection). Iterates
 * state.worldStructures; only kind 'fort' with an owner counts.
 */
function clampStepAgainstEnemyForts(
  state: GameState,
  ownTeamId: number | null,
  x0: number,
  y0: number,
  dirX: number,
  dirY: number,
  stepDist: number,
): number {
  if (ownTeamId === null) return stepDist;
  if (!state.worldStructures) return stepDist;
  let maxStep = stepDist;
  const threshold = SETTLEMENT_ZOC_RADIUS_MOVE;
  const thresholdSq = threshold * threshold;
  for (const ws of state.worldStructures.values()) {
    if (ws.kind !== 'fort') continue;
    const ownerFactionId = ws.ownerFactionId;
    if (ownerFactionId === null) continue;
    const ownerFaction = state.factions.get(ownerFactionId);
    if (!ownerFaction) continue;
    if (ownerFaction.teamId === ownTeamId) continue;
    if (ownerFaction.teamId === NEUTRAL_TEAM_ID) continue;

    const cx = ws.cell.x + 0.5;
    const cy = ws.cell.y + 0.5;
    const px = x0 - cx;
    const py = y0 - cy;
    const b = 2 * (dirX * px + dirY * py);
    const c = px * px + py * py - thresholdSq;
    if (c <= 0) return 0;
    const disc = b * b - 4 * c;
    if (disc < 0) continue;
    const sqrtDisc = Math.sqrt(disc);
    const t1 = (-b - sqrtDisc) / 2;
    const t2 = (-b + sqrtDisc) / 2;
    const firstTouch = t1 >= 0 ? t1 : t2;
    if (firstTouch >= 0 && firstTouch < maxStep) {
      maxStep = firstTouch;
    }
  }
  return maxStep;
}

/**
 * v0M7.4b (2026-07-25) — true iff a `road` WorldStructure exists on
 * the given cell. Roads are permanent + unowned (`ownerFactionId: null`);
 * any Army benefits regardless of faction per MAP.md §9.2.
 *
 * O(numWorldStructures) per call. Called once per Army per tick from
 * `advanceArmy` — at v0M7 scales (a few Roads, ≤5 Armies per faction)
 * this is trivially cheap. Index by cell later if profiling flags it.
 */
function hasRoadOnCell(state: GameState, x: number, y: number): boolean {
  if (!state.worldStructures) return false;
  for (const ws of state.worldStructures.values()) {
    if (ws.kind !== 'road') continue;
    if (ws.cell.x === x && ws.cell.y === y) return true;
  }
  return false;
}
